/**
 * AnnualUpsellCard — soft prompt shown to paid monthly members inviting
 * them to switch to annual ("2 months free"). Hidden for Signal (free)
 * and members who appear to already be annual (best-effort heuristic).
 *
 * Members on annual will not be encouraged to switch; the card will not
 * show. We don't have a definitive `billing_interval` on the profile yet,
 * so we use `subscription_renews_at` distance > 60 days as the annual
 * proxy (only renders if profile.tier !== signal AND renews_at is null
 * OR within next ~32 days).
 */
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { useProfile } from "@/lib/profile/useProfile";

const AnnualUpsellCard = () => {
  const { profile } = useProfile();
  if (!profile || profile.tier === "tier_signal") return null;

  // Heuristic: if subscription_renews_at exists and is more than 60 days
  // out, member is likely already annual — skip.
  const renews = (profile as unknown as { subscription_renews_at?: string | null })
    .subscription_renews_at;
  if (renews) {
    const days = (new Date(renews).getTime() - Date.now()) / (24 * 60 * 60 * 1000);
    if (days > 60) return null;
  }

  // Tier-aware annual savings copy (v3.1).
  const monthlyToAnnual: Record<string, { monthly: number; annual: number }> = {
    tier_pulse: { monthly: 9.99, annual: 99 },
    tier_surge: { monthly: 19.99, annual: 199 },
    tier_spectrum: { monthly: 29.99, annual: 299 },
    tier_attuned: { monthly: 99.99, annual: 999 },
  };
  const tier = monthlyToAnnual[profile.tier];
  const savings = tier ? tier.monthly * 12 - tier.annual : 0;
  const savingsLabel = savings > 0 ? `Save $${savings.toFixed(2)}/yr` : "Save 17%";

  return (
    <section className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6">
      <div className="flex items-start gap-3">
        <Sparkles className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
        <div className="flex-1">
          <h2 className="text-lg font-semibold">{savingsLabel} — switch to annual</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Two months on us when you commit for the year. Same tier. Same features. One less thing to think about.
          </p>
        </div>
        <Link
          to="/pricing?interval=annual"
          className="rounded-full bg-foreground text-background px-3 py-1.5 text-xs font-medium hover:opacity-90 transition-opacity shrink-0"
        >
          See annual pricing
        </Link>
      </div>
    </section>
  );
};

export default AnnualUpsellCard;
